import { scaleLinear } from "@visx/scale";

import { getRoundedValues } from "@app/analytics/utils/formatUtils";
import COACHING_BENCHMARKS from "@app/common/coaching/coachingBenchmarks";
import {
  AggregateCoachingItemData,
  CoachingItemAverage,
} from "@app/common/types";
import {
  DEFAULT_METRIC_THRESHOLDS,
  getSemanticScore,
  pluralize,
  pluralizePhrase,
} from "@app/common/utils";
import {
  getFillerWordsSemanticScore,
  getInterruptionsSemanticScore,
  getNonInclusiveTermsSemanticScore,
  isWpmWithinRecommendedRange,
} from "@app/common/utils/coachingUtils";

const WPM_SCORE_COLOR_RANGE = [
  "#FF4242", // red
  "#F2B02F", // orange
  "#8ABC65", // green
  "#19C79F", // green
  "#19C79F", // green
  "#8ABC65", // green
  "#F2B02F", // orange
  "#FF4242", // red
];

const WPM_DOMAIN = [
  0,
  80,
  COACHING_BENCHMARKS.wpm.min - 1,
  COACHING_BENCHMARKS.wpm.min,
  COACHING_BENCHMARKS.wpm.max,
  COACHING_BENCHMARKS.wpm.max + 1,
  210,
  240,
];

export const scaleWpmColor = scaleLinear<string>({
  domain: WPM_DOMAIN,
  range: WPM_SCORE_COLOR_RANGE,
  clamp: true,
});

export const scaleWpmDescription = scaleLinear<string>({
  domain: WPM_DOMAIN,
  range: [
    "Very slow",
    "Very slow",
    "Slightly slow",
    "In target zone",
    "In target zone",
    "Slightly fast",
    "Very fast",
    "Very fast",
  ],
  clamp: true,
});

const RELATIVE_DESCRIPTION_BY_SEMANTIC_SCORE = {
  negative: "lower than",
  neutral: "slightly lower than",
  positive: "within",
  unknown: "",
};

const INVERTED_RELATIVE_DESCRIPTION_BY_SEMANTIC_SCORE = {
  negative: "higher than",
  neutral: "slightly higher than",
  positive: "within",
  unknown: "",
};

export const getBiasDescription = (bias: number | null) => {
  const semanticScore = getSemanticScore(bias, DEFAULT_METRIC_THRESHOLDS);
  const intro =
    "Your visual sentiment towards the speaker, measured out of 100.";

  if (semanticScore === "unknown") {
    return `${intro} The target zone is ${DEFAULT_METRIC_THRESHOLDS.positive} or higher.`;
  }

  const encouragement = semanticScore === "positive" ? " Good job!" : "";

  return `${intro} Your average bias is ${bias}, which is ${RELATIVE_DESCRIPTION_BY_SEMANTIC_SCORE[semanticScore]} the target zone of ${DEFAULT_METRIC_THRESHOLDS.positive} or higher.${encouragement}`;
};

export const getCharismaDescription = (charisma: number | null) => {
  const semanticScore = getSemanticScore(charisma, DEFAULT_METRIC_THRESHOLDS);
  const intro = "Others' visual sentiment when you speak, measured out of 100.";

  if (semanticScore == "unknown") {
    return `${intro} The target zone is ${DEFAULT_METRIC_THRESHOLDS.positive} or higher.`;
  }

  const encouragement = semanticScore === "positive" ? " Nice!" : "";
  return `${intro} Your average charisma is ${charisma}, which is ${RELATIVE_DESCRIPTION_BY_SEMANTIC_SCORE[semanticScore]} the target zone of ${DEFAULT_METRIC_THRESHOLDS.positive} or higher.${encouragement}`;
};

interface DescriptionSegment {
  value: string;
  style?: React.CSSProperties;
}

export const getFillerWordsDescription = (
  rate: AggregateCoachingItemData["rate"]
): DescriptionSegment[] => {
  const semanticScore = getFillerWordsSemanticScore(rate);

  const intro = "Your usage of words that are deemed unnecessary.";

  if (semanticScore === "unknown") {
    return [
      {
        value: `${intro} The target zone is under ${COACHING_BENCHMARKS.fillerWordsThresholds.positive}%.`,
      },
    ];
  }

  const outro = semanticScore === "positive" ? " Keep it up!" : "";

  return [
    { value: `${intro} ` },
    {
      value: `${rate}% of your speech is filler words`,
      style: { fontWeight: 500 },
    },
    {
      value: `, which is ${INVERTED_RELATIVE_DESCRIPTION_BY_SEMANTIC_SCORE[semanticScore]} the target zone of under ${COACHING_BENCHMARKS.fillerWordsThresholds.positive}%.${outro}`,
    },
  ];
};

const getInterruptionFrequency = (rate: AggregateCoachingItemData["rate"]) => {
  const { formattedValue, value } = getRoundedValues(rate);
  if (formattedValue === "<1") return "<1 time";
  if (value === 1) return "once";
  if (value === 2) return "twice";
  return `${pluralizePhrase(value ?? 0, "time")}`;
};

export const getInterruptionsDescription = (
  rate: AggregateCoachingItemData["rate"]
): DescriptionSegment[] => {
  const semanticScore = getInterruptionsSemanticScore(rate);

  const intro =
    "Number of times you interrupted another participant, or where cross-talk was detected.";

  const outro =
    semanticScore === "positive"
      ? "Keep it up!"
      : "Try to limit interruptions as much as possible.";

  if (semanticScore === "unknown") {
    return [{ value: `${intro} ${outro}` }];
  }

  return [
    { value: `${intro} On average, ` },
    {
      value: `you interrupt ${getInterruptionFrequency(rate)} every 10 turns`,
      style: { fontWeight: 500 },
    },
    { value: `. ${outro}` },
  ];
};

export const getNonInclusiveTermsDescription = (
  average: CoachingItemAverage
): DescriptionSegment[] => {
  const semanticScore = getNonInclusiveTermsSemanticScore(average.value);
  const intro =
    "Your usage of words that are potentially offensive or harmful.";

  if (semanticScore === "unknown") {
    return [{ value: `${intro} Try to avoid non-inclusive terms.` }];
  }

  const outro =
    semanticScore === "positive"
      ? "Great job using inclusive language!"
      : "Try to avoid using these terms.";

  return [
    { value: `${intro} On average, ` },
    {
      value: `you use ${average.formattedValue} ${pluralize(
        "term",
        average.formattedValue === "<1" ? 1 : average.value ?? 0
      )} per meeting`,
      style: { fontWeight: 500 },
    },
    { value: `. ${outro}` },
  ];
};

export const getQuestionsAskedDescription = (
  average: CoachingItemAverage
): DescriptionSegment[] => {
  const intro = "Questions you asked during the meeting.";
  const outro =
    "Asking questions can help balance talk time and make others feel included.";

  if (average.value == null) {
    return [{ value: `${intro} ${outro}` }];
  }

  return [
    { value: `${intro} On average, ` },
    {
      value: `you ask ${average.formattedValue} ${pluralize(
        "question",
        average.formattedValue === "<1" ? 1 : average.value ?? 0
      )} per meeting`,
      style: { fontWeight: 500 },
    },
    {
      value: `. ${outro}`,
    },
  ];
};

const getWpmTargetZoneDescription = (wpm: number) => {
  if (isWpmWithinRecommendedRange(wpm)) return "within";
  return wpm < COACHING_BENCHMARKS.wpm.min ? "below" : "above";
};

export const getWpmDescription = (wpm: number | null) => {
  const intro =
    "Your average rate of speech measured in words per minute (WPM).";

  if (wpm) {
    const encouragement = isWpmWithinRecommendedRange(wpm)
      ? " Keep it up!"
      : "";

    return [
      { value: `${intro} ` },
      { value: `You speak at ${wpm} WPM`, style: { fontWeight: 500 } },
      {
        value: `, which is ${getWpmTargetZoneDescription(
          wpm
        )} the recommended range of ${COACHING_BENCHMARKS.wpm.min}-${
          COACHING_BENCHMARKS.wpm.max
        }.${encouragement}`,
      },
    ];
  }

  return [
    {
      value: `${intro} The target zone is ${COACHING_BENCHMARKS.wpm.min}-${COACHING_BENCHMARKS.wpm.max} WPM.`,
    },
  ];
};
